import type { ReactNode } from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { clamp } from "./trial-dates";

type StepperProps = {
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  disabled?: boolean;
};

export const Stepper = ({ value, min, max, onChange, disabled }: StepperProps) => (
  <div className="inline-flex items-stretch rounded-md border border-border bg-card">
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="rounded-r-none border-r border-border text-lime-900 hover:bg-lime-600/10"
      onClick={() => onChange(clamp(value - 1, min, max))}
      disabled={disabled || value <= min}
    >
      <Minus className="h-4 w-4" />
    </Button>
    <Input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        onChange(clamp(isNaN(v) ? min : v, min, max));
      }}
      disabled={disabled}
      className="w-14 rounded-none border-0 text-center font-bold focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      className="rounded-l-none border-l border-border text-lime-900 hover:bg-lime-600/10"
      onClick={() => onChange(clamp(value + 1, min, max))}
      disabled={disabled || value >= max}
    >
      <Plus className="h-4 w-4" />
    </Button>
  </div>
);

type ChipProps = {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
};

export const Chip = ({ active, onClick, disabled, children }: ChipProps) => (
  <Button
    type="button"
    variant="outline"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "h-10 w-14 rounded-xl text-sm font-semibold",
      active
        ? "border-lime-600/40 bg-lime-600/10 text-lime-900 hover:bg-lime-600/15"
        : "border-border bg-background text-foreground hover:bg-muted",
    )}
  >
    {children}
  </Button>
);
